import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  Globe,
  TrendingUp,
  TrendingDown,
  Timer,
  CheckSquare,
  SlidersHorizontal,
  BarChart3,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const fmtInt = (n) => new Intl.NumberFormat('en-GB').format(n);

function fmtStat(stat) {
  if (!stat) return '—';
  if (stat.format === 'duration') {
    const s = stat.value_seconds;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}m ${String(r).padStart(2, '0')}s`;
  }
  if (stat.format === 'percent_1dp') return `${stat.value.toFixed(1)}%`;
  if (stat.format === 'percent_0dp') return `${Math.round(stat.value)}%`;
  return fmtInt(stat.value);
}

// ─── Stat card ─────────────────────────────────────────────────────
function StatCard({ label, period, icon: Icon, value, delta, deltaDirection, testId }) {
  const isUp = deltaDirection === 'up';
  const Trend = isUp ? TrendingUp : TrendingDown;
  const trendColor = isUp ? 'var(--ok)' : 'var(--danger)';
  const trendBg = isUp ? 'var(--ok-10)' : 'var(--danger-10)';
  return (
    <div data-testid={testId} className="p-5 rounded-lg" style={CARD_STYLE}>
      <div className="flex items-start justify-between mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
          {label}{period && <span style={{ color: 'var(--te)' }}> · {period}</span>}
        </p>
        <div
          className="flex items-center justify-center rounded"
          style={{ width: 28, height: 28, background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
        >
          <Icon size={14} style={{ color: 'var(--a)' }} />
        </div>
      </div>
      <p className="font-mono text-3xl font-semibold mb-3" style={{ color: 'var(--white)' }}>
        {value}
      </p>
      {delta != null ? (
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
            style={{ background: trendBg, color: trendColor }}
          >
            <Trend size={11} />
            <span className="font-mono text-[11px]">
              {delta > 0 ? '+' : ''}{delta.toFixed(0)}%
            </span>
          </span>
        </div>
      ) : (
        <span
          className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded"
          style={{ color: 'var(--ts)', background: 'var(--neutral-10)' }}
        >
          no trend
        </span>
      )}
    </div>
  );
}

// ─── Progress bar (shared by traffic sources and funnel) ──────────
function BarRow({ label, percentage, showPercentColour, testId }) {
  let pctColor = 'var(--a)';
  if (showPercentColour) {
    if (percentage >= 80) pctColor = 'var(--ok)';
    else if (percentage >= 50) pctColor = 'var(--warn)';
    else pctColor = 'var(--danger)';
  }
  return (
    <div data-testid={testId} className="py-2">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-body text-sm" style={{ color: 'var(--white)' }}>{label}</span>
        <span className="font-mono text-xs" style={{ color: pctColor }}>{percentage}%</span>
      </div>
      <div
        className="rounded"
        style={{ height: 4, background: 'var(--a-10)', borderRadius: 2 }}
      >
        <div
          className="h-full"
          style={{
            width: `${percentage}%`,
            background: 'linear-gradient(90deg, var(--a), var(--violet))',
            borderRadius: 2,
            transition: 'width 240ms ease-out',
          }}
        />
      </div>
    </div>
  );
}

// ─── 30-day sparkline ─────────────────────────────────────────────
function PageViews30DayChart({ data }) {
  const maxV = useMemo(() => Math.max(...data.map((d) => d.views)) || 1, [data]);
  const width = 900;
  const height = 140;
  const topPad = 8;
  const labelPad = 22;
  const chartH = height - topPad - labelPad;
  const gap = 3;
  const barWidth = (width - gap * (data.length - 1)) / data.length;
  const firstIdx = 0;
  const lastIdx = data.length - 1;
  const midIdx = Math.floor(data.length / 2);
  const labelIdxs = new Set([firstIdx, midIdx, lastIdx]);
  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      role="img"
      aria-label="Page views, last 30 days"
      data-testid="admin-analytics-sparkline"
      preserveAspectRatio="none"
    >
      {data.map((d, i) => {
        const h = Math.max(2, (d.views / maxV) * chartH);
        const x = i * (barWidth + gap);
        const y = topPad + (chartH - h);
        return (
          <g key={d.date}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={h}
              rx={2}
              fill="var(--a)"
              opacity={d.is_today ? 1 : 0.30}
            >
              <title>{`${formatDate(d.date)} · ${d.views} views`}</title>
            </rect>
            {labelIdxs.has(i) && (
              <text
                x={x + barWidth / 2}
                y={height - 6}
                textAnchor="middle"
                className="font-mono"
                fontSize="0.58rem"
                fill="var(--ts)"
                style={{ letterSpacing: '0.08em' }}
              >
                {formatDate(d.date)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Main section ─────────────────────────────────────────────────
export default function AnalyticsSection() {
  const { adminFetch } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/analytics`);
      if (!res.ok) throw new Error(`Load failed: ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="p-8 rounded-lg" style={CARD_STYLE}>
      <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>Loading analytics…</p>
    </div>;
  }
  if (error && !data) {
    return <div className="p-8 rounded-lg" style={CARD_STYLE}>
      <p className="font-mono text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
    </div>;
  }
  if (!data) return null;

  const { stats, traffic_sources, funnel, page_views_30d } = data;
  const totalViews = page_views_30d.reduce((a, d) => a + d.views, 0);

  return (
    <div className="space-y-5" data-testid="admin-analytics-section">
      {/* Header */}
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
          section · analytics
        </p>
        <h2 className="font-display text-2xl font-bold mt-1" style={{ color: 'var(--white)' }}>
          Analytics
        </h2>
      </div>

      {/* 4 stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-analytics-summary">
        <StatCard
          label={stats.total_visits_30d.label}
          period={stats.total_visits_30d.period}
          icon={Globe}
          value={fmtStat(stats.total_visits_30d)}
          delta={stats.total_visits_30d.delta_pct}
          deltaDirection={stats.total_visits_30d.delta_direction}
          testId="admin-analytics-stat-visits"
        />
        <StatCard
          label={stats.lead_conversion_rate.label}
          icon={TrendingUp}
          value={fmtStat(stats.lead_conversion_rate)}
          delta={stats.lead_conversion_rate.delta_pct}
          deltaDirection={stats.lead_conversion_rate.delta_direction}
          testId="admin-analytics-stat-conversion"
        />
        <StatCard
          label={stats.avg_time_on_site.label}
          icon={Timer}
          value={fmtStat(stats.avg_time_on_site)}
          delta={null}
          testId="admin-analytics-stat-time"
        />
        <StatCard
          label={stats.quiz_completion_rate.label}
          icon={CheckSquare}
          value={fmtStat(stats.quiz_completion_rate)}
          delta={stats.quiz_completion_rate.delta_pct}
          deltaDirection={stats.quiz_completion_rate.delta_direction}
          testId="admin-analytics-stat-quiz"
        />
      </div>

      {/* Traffic sources + Funnel (side-by-side on wide) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-6 rounded-lg" style={CARD_STYLE} data-testid="admin-analytics-traffic">
          <div className="mb-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>sources</p>
            <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
              Top Traffic Sources
            </h3>
          </div>
          <div>
            {traffic_sources.map((s) => (
              <BarRow
                key={s.label}
                label={s.label}
                percentage={s.percentage}
                testId={`admin-traffic-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              />
            ))}
          </div>
        </div>

        <div className="p-6 rounded-lg" style={CARD_STYLE} data-testid="admin-analytics-funnel">
          <div className="mb-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>funnel</p>
            <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
              <SlidersHorizontal size={14} className="inline -mt-0.5 mr-1.5" style={{ color: 'var(--a)' }} />
              Pricing Flow Drop-off
            </h3>
          </div>
          <div>
            {funnel.map((step, i) => (
              <BarRow
                key={step.step}
                label={`${i + 1}. ${step.step}`}
                percentage={step.percentage}
                showPercentColour
                testId={`admin-funnel-step-${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 30-day sparkline */}
      <div className="p-6 rounded-lg" style={CARD_STYLE} data-testid="admin-analytics-views30">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>trend</p>
            <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
              <BarChart3 size={14} className="inline -mt-0.5 mr-1.5" style={{ color: 'var(--a)' }} />
              Page Views — Last 30 Days
            </h3>
          </div>
          <span
            data-testid="admin-analytics-views-total"
            className="font-mono text-xs"
            style={{ color: 'var(--a)' }}
          >
            {fmtInt(totalViews)} total visits
          </span>
        </div>
        <PageViews30DayChart data={page_views_30d} />
      </div>
    </div>
  );
}
